<script setup>
// core
import {computed} from "vue";
import {useStore} from "vuex";

// components
import BaseRibbon45Rotate from "@/components/ui/BaseRibbon45Rotate.vue";

const store = useStore();

const navBarMenu = computed(() => store.getters["navBar/navBarMenu"])
const activeKeys = computed(() => store.getters["navBar/activeKeys"])

const navIcon = (icon) => {
    return icon.startsWith('bi')
        ? `bi ${icon}`
        : `icon-${icon}`
}

const isChildrenVisible = (navItem) => {
    return Object.hasOwn(navItem, 'children') && activeKeys.value.includes(navItem.key)
}
</script>

<template>
    <ul class="_navbar">
        <li
            v-for="nav in navBarMenu"
            :key="nav.key"
            class="_navbar__item"
            :class="{ 'is-active': activeKeys.includes(nav.key) }"
            @click="store.dispatch('navBar/selectItem', nav)"
        >
            <div class="_navbar__item-icon">
                <component v-if="!!nav?.svg" :is="nav.svg" style="width: 26px; height: 26px;"/>
                <i v-else :class="navIcon(nav.icon)"></i>
                <BaseRibbon45Rotate
                    v-if="!!nav?.ribbon"
                    :text="nav.ribbon"
                    :color="nav.ribbonColor"
                />
            </div>
            <h6 class="_navbar__item-title">
                {{ $t(`general.menu.${nav.key}`) }}
            </h6>
            <ul v-if="isChildrenVisible(nav)" class="_navbar__children">
                <li
                    v-for="child in nav.children"
                    :key="child.key"
                    class="_navbar__children-item"
                    :class="{ 'is-active': activeKeys.includes(child.key) }"
                    @click.stop="store.dispatch('navBar/selectItem', child)"
                >
                    <span>{{ $t(`general.menu.subs.${nav.key}_${child.key}`) }}</span>
                </li>
            </ul>
        </li>
    </ul>
</template>

<style scoped lang="scss">
._navbar {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 0 15px;
        width: 100%;
        transition: 0.3s;

        &-icon {
            position: relative;
            width: 66px;
            height: 66px;

            border-radius: 4px;
            background-color: rgba(white, 0.07);

            display: flex;
            align-items: center;
            justify-content: center;

            font-size: 26px;
            color: rgba(white, 0.17);

            transition: 0.3s;
        }

        &-title {
            width: 66px;
            padding: 0;
            margin: auto;
            display: block;
            font-family: Gilroy;
            font-weight: 500;
            font-size: 10px;
            line-height: 17px;
            text-transform: uppercase;
            color: white;
            transition: 0.3s;
        }

        &.is-active,
        &:hover {
            cursor: pointer;

            ._navbar__item-icon,
            ._navbar__item-title {
                color: var(--general-color);
                transition: 0.3s;
            }
        }

        &.is-active {
            padding: 0;
            width: min-content;

            ._navbar__item-icon {
                position: relative;
                width: calc(66px + 15px);
                transition: 0.3s;

                &::before {
                    content: "";

                    height: 100%;
                    width: 6px;

                    position: absolute;
                    left: 0;
                    top: 0;

                    background-color: var(--general-color);
                }
            }
        }
    }

    &__children {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;

        &-item {
            width: 55px;
            text-align: left;
            position: relative;

            span {
                color: #ffffff;
                display: block;
                font-family: Gilroy;
                font-size: 10px;
                font-style: normal;
                font-weight: 500;

                transition: 0.3s;
            }

            &:hover {
                span {
                    cursor: pointer;
                    color: var(--general-color);
                }
            }

            &.is-active {
                cursor: default;
                padding-left: 10px;

                &::before {
                    content: "";
                    height: 80%;
                    width: 6px;
                    position: absolute;
                    left: 0;
                    top: 2px;
                    background-color: var(--general-color);
                }

                span {
                    color: var(--general-color)
                }
            }
        }
    }
}

@media screen and (max-width: 640px) {
    ._navbar {
        &__item {
            padding: 0;

            &-title {
                font-size: 8px;
            }

            &-icon {
                width: 37px;
                height: 37px;
                font-size: 18px;
            }

            &.is-active {
                ._navbar__item-icon {
                    transition: 0.3s;
                    width: 66px;

                    &::before {
                        width: 2px;
                    }
                }
            }
        }

        &__children {
            &-item {
                padding-left: 4px;
                font-size: 8px;

                &.is-active {
                    padding-left: 4px;

                    &::before {
                        width: 2px;
                    }
                }
            }
        }
    }
}
</style>
